<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import { formatSubmittedAt, getIdempotencyKey } from '@shared/utils'
import FiatWalletTab from '../components/FiatWalletTab.vue'
import WithdrawalErrorModal from '../components/WithdrawalErrorModal.vue'
import { useTransactionsQuery } from '../../transactions/api/queries/useTransactionsQuery'
import type { Transaction, TransactionFilters } from '../../transactions/types/transactionTypes'
import { useFiatWalletQuery } from '../api/queries/useFiatWalletQuery'
import { useSlicealAccountQuery } from '../api/queries/useSlicealAccountQuery'
import { useSubmitWithdrawalMutation } from '../api/mutations/useSubmitWithdrawalMutation'
import { useAddFundsMutation } from '../api/mutations/useAddFundsMutation'
import { canTransactInWallet } from '../utils/walletAccessUtils'
import { useToastStore } from '@uiKit/store/toastStore'
import { getErrorMessage } from '@core/api/normalizeError'
import {
  useBankAccountsQuery,
  useAccountAccessRestriction,
  useKycActionState,
} from '@modules/account'

const router = useRouter()
const route = useRoute()
const toastStore = useToastStore()

const { isRestricted } = useAccountAccessRestriction()
const { kycActionState } = useKycActionState()

const fiatQuery = useFiatWalletQuery()
const bankAccountsQuery = useBankAccountsQuery()
const slicealAccountQuery = useSlicealAccountQuery()

const currency = computed(() => fiatQuery.display.value?.currency ?? 'AED')
const balance = computed(() => fiatQuery.display.value?.balance ?? '')
const balanceAmount = computed(() =>
  parseFloat((fiatQuery.display.value?.balance ?? '0').replace(/,/g, '')),
)
const lastUpdated = computed(() => fiatQuery.display.value?.lastUpdated ?? '')
const walletLoading = computed(() => fiatQuery.isLoading.value)
// Background refetch only — the first load is covered by `walletLoading`, so
// the refresh button spins without the balance collapsing into a skeleton.
const walletRefreshing = computed(() => fiatQuery.isFetching.value && !fiatQuery.isLoading.value)
const walletHasError = computed(() => fiatQuery.isError.value)

const onRetryWallet = () => {
  fiatQuery.refetch()
}

const bankAccounts = computed(() => bankAccountsQuery.data.value ?? [])
const hasBankAccount = computed(() => bankAccounts.value.length > 0)
const hasActiveBank = computed(() =>
  bankAccounts.value.some((account) => account.accountStatus === 'verified'),
)
const defaultBankAccount = computed(
  () =>
    bankAccounts.value.find(
      (account) => account.isPrimary && account.accountStatus === 'verified',
    ) ??
    bankAccounts.value.find((account) => account.accountStatus === 'verified') ??
    bankAccounts.value[0] ??
    null,
)

// ── Activity feed ──────────────────────────────────────────────────────────
// The wallet screen renders the unbroken ledger grouped by month. Filtering,
// sorting and export live on the (not yet designed) "See All" screen, so this
// query runs on the server's default ordering and nothing else.
const activityFilters = computed<TransactionFilters>(() => ({
  search: '',
  type: '',
  status: '',
  sort: 'newest_first',
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
}))

const transactionsQuery = useTransactionsQuery(activityFilters)

const activityTransactions = computed(
  () => transactionsQuery.data.value?.pages.flatMap((page) => page.rows) ?? [],
)
const activityHasMore = computed(() => Boolean(transactionsQuery.hasNextPage.value))
const activityLoading = computed(() => transactionsQuery.isLoading.value)
const activityLoadingMore = computed(() => transactionsQuery.isFetchingNextPage.value)

const onLoadMore = () => {
  if (transactionsQuery.hasNextPage.value && !transactionsQuery.isFetchingNextPage.value) {
    transactionsQuery.fetchNextPage()
  }
}

// ── Deposit ────────────────────────────────────────────────────────────────
const addFundsMutation = useAddFundsMutation()
const depositSubmitting = computed(() => addFundsMutation.isPending.value)
const depositError = ref<string | null>(null)
const fiatWalletTabRef = ref<InstanceType<typeof FiatWalletTab> | null>(null)

// Set once the deposit is accepted — the modal watches it and advances to its
// "Submission Received" step, so the receipt stays inside the flow instead of
// routing away to a separate success page.
const depositSubmittedAt = ref('')
// POST /wallets/fiat/deposits requires utrNumber, but the design has no field
// for it — so the FE files the deposit under its own generated reference and
// shows that same value on the receipt. Swap this for the investor's bank
// reference if the field ever returns to the confirm step.
const depositReference = ref('')

const onDepositConfirm = async (payload: { amount: number; attachment: File }) => {
  depositError.value = null
  const reference = `SL-DEP-${getIdempotencyKey()}`
  try {
    await addFundsMutation.mutateAsync({
      amount: payload.amount,
      idempotencyKey: reference,
      utrNumber: reference,
      transactionDocument: payload.attachment,
    })
    depositReference.value = reference
    depositSubmittedAt.value = formatSubmittedAt()
  } catch (e) {
    depositError.value =
      getErrorMessage(e, 'Failed to confirm transfer. Please try again.')
  }
}

const onDepositClearError = () => {
  depositError.value = null
  depositSubmittedAt.value = ''
  depositReference.value = ''
}

const onViewTransactionHistory = () => {
  router.push(routes.WALLET_TRANSACTIONS)
}

// ── Withdrawal ─────────────────────────────────────────────────────────────
const withdrawMutation = useSubmitWithdrawalMutation()
const withdrawing = computed(() => withdrawMutation.isPending.value)

const withdrawErrorVisible = ref(false)
const withdrawErrorMessage = ref('')

// The design replaces the old success modal with a confirmation toast plus a
// jump into the ledger, where the new pending withdrawal is now the top row.
const onWithdraw = async (payload: { amount: number; bankAccountId: string }) => {
  try {
    await withdrawMutation.mutateAsync({
      amount: payload.amount,
      idempotencyKey: getIdempotencyKey(),
      bankAccountId: payload.bankAccountId,
    })
    toastStore.addToast({
      message:
        'Withdrawal confirmed. The money will be transferred to your bank account within 48 hours',
      type: 'success',
    })
    router.push(routes.WALLET_TRANSACTIONS)
  } catch (e) {
    withdrawErrorMessage.value =
      getErrorMessage(e, 'Something went wrong. Please try again.')
    withdrawErrorVisible.value = true
  }
}

const onAddBank = () => {
  router.push(routes.ADD_BANK_ACCOUNT)
}

const openDetail = (tx: Transaction) => {
  router.push(routes.WALLET_TRANSACTION_DETAIL.replace(':id', tx.id))
}

// Deep-link entry: callers from outside (dashboard, transactions empty state)
// navigate to /wallet?action=deposit. Auto-open the Add Funds modal once the
// tab is mounted, then strip the query so back-navigation doesn't re-open it.
watch(
  () => route.query.action,
  (action) => {
    if (action !== 'deposit') return
    const cleaned = { ...route.query }
    delete cleaned.action
    // Honour the same money-moving gate the buttons use — a restricted or
    // unverified account ignores the deep link but still strips the query so
    // it doesn't keep re-triggering.
    if (
      canTransactInWallet({
        restricted: isRestricted.value,
        kycState: kycActionState.value,
        hasBankAccount: hasBankAccount.value,
        hasActiveBank: hasActiveBank.value,
      })
    ) {
      fiatWalletTabRef.value?.openDeposit()
    }
    router.replace({ path: route.path, query: cleaned })
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <Box direction="col" class="fiat-wallet-container">
    <FiatWalletTab
      ref="fiatWalletTabRef"
      :restricted="isRestricted"
      :kyc-state="kycActionState"
      :currency="currency"
      :balance="balance"
      :balance-amount="balanceAmount"
      :last-updated="lastUpdated"
      :wallet-loading="walletLoading"
      :wallet-refreshing="walletRefreshing"
      :wallet-has-error="walletHasError"
      :bank-accounts="bankAccounts"
      :default-bank-account="defaultBankAccount"
      :sliceal-account="slicealAccountQuery.data.value ?? null"
      :activity-transactions="activityTransactions"
      :activity-loading="activityLoading"
      :activity-loading-more="activityLoadingMore"
      :activity-has-more="activityHasMore"
      :withdrawing="withdrawing"
      :deposit-submitting="depositSubmitting"
      :deposit-error="depositError"
      :deposit-submitted-at="depositSubmittedAt"
      :deposit-reference="depositReference"
      @deposit-confirm="onDepositConfirm"
      @deposit-clear-error="onDepositClearError"
      @deposit-view-history="onViewTransactionHistory"
      @withdraw="onWithdraw"
      @add-bank="onAddBank"
      @view-details="openDetail"
      @retry-wallet="onRetryWallet"
      @load-more="onLoadMore"
    />


    <WithdrawalErrorModal
      v-model:visible="withdrawErrorVisible"
      :message="withdrawErrorMessage"
    />
  </Box>
</template>

<style scoped>
.fiat-wallet-container {
  width: 100%;
}
</style>
